import { format } from 'date-fns';

import {
  FormAnswer,
  FormAnswerChangeLogEntry,
  FormAnswerChatMessage,
  FormAnswerStatus,
  FormAnswerStatusColors,
  PreviousPeriodCountAnswersResponse,
  PreviousPeriodCountTaskAssignsResponse,
  ResumeExportType,
  ResumeStatisticsResponse,
  ResumeTaskForm,
  ResumeTaskFormsResponse,
  TaskAssignsStatsResponse,
} from '../types';
import { client } from './index';

async function getResumeStatistics() {
  const response = await client.get<ResumeStatisticsResponse>('/resume_statistics');
  const { data } = response;
  return data;
}

async function getFormAnswerStatuses() {
  const response = await client.get<FormAnswerStatus[]>('/all_form_states');
  const { data } = response;
  return data;
}

async function createFormAnswerStatus(name: string) {
  const response = await client.post<{ result: 'ok' | 'error'; new_id: number }>(
    '/create_form_state',
    {
      name,
    },
  );
  const { data } = response;
  return data;
}

async function editFormAnswerStatus(
  statusId: FormAnswerStatus['id'],
  name: string,
  color: FormAnswerStatusColors,
) {
  const response = await client.patch(`/edit_form_state`, {
    id: statusId,
    name,
    color,
  });
  const { data } = response;
  return data;
}

async function deleteFormAnswerStatus(statusId: FormAnswerStatus['id']) {
  const response = await client.delete(`/delete_form_state?id=${statusId}`);
  const { data } = response;
  return data;
}

async function updateFormAnswerStatus(
  formAnswerId: FormAnswer['id'],
  statusId: FormAnswerStatus['id'] | 'delete' | 'clean' | 'recover',
) {
  const response = await client.post('/change_form_state', {
    form_id: formAnswerId,
    form_state_id: statusId ?? 'clean',
  });
  const { data } = response;
  return data;
}

async function bulkUpdateFormAnswerStatus(
  formAnswerIds: FormAnswer['id'][],
  statusId?: FormAnswerStatus['id'],
) {
  const response = await client.post('/bulk_change_form_state', {
    form_ids: formAnswerIds,
    form_state_id: statusId ?? 'clean',
  });
  const { data } = response;
  return data;
}

async function getFormAnswerChangeLog(
  formAnswerId: FormAnswer['id'],
  formId: FormAnswer['form_id'],
) {
  const response = await client.get<{ form_history: FormAnswerChangeLogEntry[] }>(
    `/task_forms/${formId}/form_answers/${formAnswerId}/history`,
  );
  const { data } = response;
  return data;
}

async function notifyUsersFromFormAnswerChat(
  message: string,
  userIds: FormAnswerChatMessage['user']['_id'][],
) {
  const response = await client.post('notify', { msg_body: message, user_ids: userIds });
  const { data } = response;
  return data;
}

async function getTaskFormsWithSearch(page: number, searchQuery?: string) {
  const params = new URLSearchParams();

  if (page) params.append('page', page.toString());
  if (searchQuery) params.append('search', searchQuery);

  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `/resume_forms${queryString}`;

  const response = await client.get<ResumeTaskFormsResponse>(url);
  const { data } = response;
  return data;
}

async function getTaskForm(taskFormId: number) {
  const getTaskFormUrl = `/resume_form/${taskFormId}`;
  const response = await client.get<{ form: ResumeTaskForm }>(getTaskFormUrl);
  const { data } = response;
  return data;
}

async function sendBackupToEmails(emails: string[], formAnswerId: number) {
  const response = await client.post('/alerts.json', {
    emails: emails.join(','),
    form_id: formAnswerId,
    create_new_alert: true,
  });
  const { data } = response;
  return data;
}

async function bulkDeleteFormAnswers(formAnswerIds: FormAnswer['id'][]) {
  const response = await client.post('/bulk_delete_form_answers', {
    form_ids: formAnswerIds,
  });
  const { data } = response;
  return data;
}

async function bulkRecoverFormAnswers(formAnswerIds: FormAnswer['id'][]) {
  const response = await client.post('/bulk_recover_form_answers', {
    form_ids: formAnswerIds,
  });
  const { data } = response;
  return data;
}

export type SendToGoogleSheetsResult =
  | { state: 'success' }
  | { state: 'not_found' }
  | { state: 'gsheet_busy' }
  | { state: 'integration' }
  | { state: 'confirm_required' }
  | { state: 'unknown' };

type SendToSheetResponseBody = {
  status: string;
  code_status?: string;
};

async function sendFormAnswerToGoogleSheets(
  formAnswerId: FormAnswer['id'],
  confirmed = false,
): Promise<SendToGoogleSheetsResult> {
  const url = confirmed
    ? `/send_to_sheet?form_answer_id=${formAnswerId}&confirmed=true`
    : `/send_to_sheet?form_answer_id=${formAnswerId}`;
  const response = await client.post<SendToSheetResponseBody>(
    url,
    {},
    { validateStatus: () => true },
  );
  const httpStatus = response.status;
  const { status: bodyStatus, code_status: codeStatus } = response.data ?? {};

  if (httpStatus === 404 || bodyStatus === 'not found') {
    return { state: 'not_found' } satisfies SendToGoogleSheetsResult;
  }

  if (httpStatus >= 200 && httpStatus < 300 && bodyStatus === 'ok') {
    return { state: 'success' } satisfies SendToGoogleSheetsResult;
  }

  if (httpStatus === 202 && bodyStatus === 'send_modify_inactive') {
    return { state: 'confirm_required' } satisfies SendToGoogleSheetsResult;
  }

  const busyHttp = httpStatus === 429 || httpStatus === 503;
  const busyCode =
    codeStatus === 'too_many_requests' || codeStatus === 'service_unavailable';
  const busyBody =
    bodyStatus === 'RateLimitExceeded' ||
    bodyStatus === 'ServerError' ||
    bodyStatus === 'ParallelUpdate';
  if (busyHttp || busyCode || busyBody) {
    return { state: 'gsheet_busy' } satisfies SendToGoogleSheetsResult;
  }

  const integrationHttp = httpStatus === 400;
  const integrationBody =
    bodyStatus === 'ClientError' ||
    bodyStatus === 'Webhook URL not found' ||
    bodyStatus === 'FormAnswerNotMatching';
  if (integrationHttp || integrationBody) {
    return { state: 'integration' } satisfies SendToGoogleSheetsResult;
  }

  return { state: 'unknown' } satisfies SendToGoogleSheetsResult;
}

async function duplicateFormAnswer(formAnswerId: FormAnswer['id']) {
  const response = await client.post(`/form_answer/duplicate?id=${formAnswerId}`);
  const { data } = response;
  return data;
}

async function obtainLocationEmails(formAnswerId: number) {
  const response = await client.get<{ emails: string[] }>(
    `/form_answers/${formAnswerId}/location_emails`,
  );
  const { data } = response;
  return data;
}

async function exportFormAnswers(
  email: string,
  exportType: ResumeExportType,
  formId: number[],
  from: Date,
  to: Date,
  locationId?: number[],
  locationType?: 'locations' | 'nestable_locations',
  userId?: number[],
  statusId?: number[],
  question?: string,
  answer?: string,
  pdfFormAnswers?: FormAnswer['id'][],
) {
  const response = await client.post('/export_form_answers', {
    email,
    type_export: exportType,
    task_form_id: formId,
    start: from ? format(from, 'dd/MM/yyyy') : undefined,
    end: to ? format(to, 'dd/MM/yyyy') : undefined,
    ...(locationId && locationId.length > 0 && { location: locationId }),
    ...(locationType && { location_type: locationType }),
    ...(userId && userId.length > 0 && { user_id: userId }),
    ...(statusId && statusId.length > 0 && { form_state: statusId }),
    ...(question && { search_question_name: question }),
    ...(answer && { search_value: answer }),
    ...(pdfFormAnswers && { page_ids: pdfFormAnswers.join(',') }),
  });
  const { data } = response;
  return data;
}

async function getTaskAssignsStats(
  startDate?: string,
  endDate?: string,
  userId?: number[],
  statusId?: number[],
  code?: number,
  locationId?: number[],
  formId?: number[],
) {
  const params = new URLSearchParams();

  if (startDate) params.append('start', startDate);
  if (endDate) params.append('end', endDate);
  if (userId && userId.length > 0) params.append('user_id', userId.join(','));
  if (statusId && statusId.length > 0) params.append('state', statusId.join(','));
  if (code) params.append('code', code.toString());
  if (locationId && locationId.length > 0) params.append('location_id', locationId.join(','));
  if (formId && formId.length > 0) params.append('task_form_id', formId.join(','));

  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `/task_assigns_react_resume${queryString}`;

  const response = await client.get<TaskAssignsStatsResponse>(url);
  const { data } = response;
  return data;
}

async function getAccountInformation() {
  const response = await client.get('/account_info');
  const { data } = response;
  return data;
}

async function getPreviousPeriodCountAnswers(startDate: string, endDate: string) {
  const params = new URLSearchParams();
  params.append('start', startDate);
  params.append('end', endDate);

  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `/form_answers/previous_period_count${queryString}`;

  const response = await client.get<PreviousPeriodCountAnswersResponse>(url);
  const { data } = response;
  return data;
}

async function getPreviousPeriodCountTaskAssigns(startDate: string, endDate: string) {
  const params = new URLSearchParams();
  params.append('start', startDate);
  params.append('end', endDate);

  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `/task_assigns_react/previous_period_count${queryString}`;

  const response = await client.get<PreviousPeriodCountTaskAssignsResponse>(url);
  const { data } = response;
  return data;
}

export const resumeApi = {
  getResumeStatistics,
  getFormAnswerStatuses,
  createFormAnswerStatus,
  editFormAnswerStatus,
  deleteFormAnswerStatus,
  updateFormAnswerStatus,
  bulkUpdateFormAnswerStatus,
  getFormAnswerChangeLog,
  notifyUsersFromFormAnswerChat,
  getTaskFormsWithSearch,
  getTaskForm,
  sendBackupToEmails,
  obtainLocationEmails,
  bulkDeleteFormAnswers,
  bulkRecoverFormAnswers,
  sendFormAnswerToGoogleSheets,
  duplicateFormAnswer,
  exportFormAnswers,
  getTaskAssignsStats,
  getAccountInformation,
  getPreviousPeriodCountAnswers,
  getPreviousPeriodCountTaskAssigns,
};
